import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

const STATUS_LABELS = { open: '🟢 Açık', closed: '🔴 Kapalı', suspended: '🟡 Askıda' };
const PHASE_COLORS = { 'I': '#7c3aed', 'II': '#0284c7', 'III': '#0891b2', 'IV': '#059669' };
const ARM_COLORS = ['#0891b2', '#7c3aed', '#059669', '#d97706', '#dc2626', '#6366f1'];

export default function TrialList() {
  const [trials, setTrials] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('card');
  const [search, setSearch] = useState('');
  const [filterDisease, setFilterDisease] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterPhase, setFilterPhase] = useState('');
  const [filterStatus, setFilterStatus] = useState('open');
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    const { data: trialsData } = await supabase
      .from('trials')
      .select('*, branches(name), trial_arms(id, name, agents), trial_investigators(id, center_name, city, investigator, phone, email), trial_diseases(disease_id, diseases(id, name, code))')
      .order('created_at', { ascending: false });
    const { data: diseasesData } = await supabase.from('diseases').select('*').order('name');
    const { data: branchesData } = await supabase.from('branches').select('*').order('id');
    setTrials(trialsData || []);
    setDiseases(diseasesData || []);
    setBranches(branchesData || []);
    setLoading(false);
  }

  const filtered = trials.filter(t => {
    const trialDiseases = t.trial_diseases?.map(td => td.diseases) || [];
    const matchSearch = !search ||
      t.title?.toLowerCase().includes(search.toLowerCase()) ||
      t.short_code?.toLowerCase().includes(search.toLowerCase()) ||
      t.nct_number?.toLowerCase().includes(search.toLowerCase()) ||
      trialDiseases.some(d => d?.name?.toLowerCase().includes(search.toLowerCase()) || d?.code?.toLowerCase().includes(search.toLowerCase())) ||
      t.trial_arms?.some(a => a.agents?.toLowerCase().includes(search.toLowerCase())) ||
      t.trial_investigators?.some(inv => inv.center_name?.toLowerCase().includes(search.toLowerCase()) || inv.investigator?.toLowerCase().includes(search.toLowerCase()));
    const matchDisease = !filterDisease || trialDiseases.some(d => d?.id === parseInt(filterDisease));
    const matchBranch = !filterBranch || t.branch_id === parseInt(filterBranch);
    const matchPhase = !filterPhase || t.phase === filterPhase;
    const matchStatus = !filterStatus || t.status === filterStatus;
    return matchSearch && matchDisease && matchBranch && matchPhase && matchStatus;
  });

  if (loading) return <div style={S.center}>Yükleniyor...</div>;

  return (
    <div style={S.container}>
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Klinik Çalışmalar</h2>
          <p style={S.subtitle}>{filtered.length} çalışma listeleniyor</p>
        </div>
        <div style={S.viewToggle}>
          <button style={{ ...S.toggleBtn, background: viewMode === 'card' ? '#1e3a5f' : '#e2e8f0', color: viewMode === 'card' ? 'white' : '#475569' }} onClick={() => setViewMode('card')}>⊞ Kart</button>
          <button style={{ ...S.toggleBtn, background: viewMode === 'table' ? '#1e3a5f' : '#e2e8f0', color: viewMode === 'table' ? 'white' : '#475569' }} onClick={() => setViewMode('table')}>☰ Liste</button>
        </div>
      </div>

      <div style={S.filters}>
        <input style={S.searchInput} placeholder="🔍 Çalışma adı, NCT no, ilaç, merkez, araştırmacı, hastalık..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={S.select} value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
          <option value="">Tüm Branşlar</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select style={S.select} value={filterDisease} onChange={e => setFilterDisease(e.target.value)}>
          <option value="">Tüm Hastalıklar</option>
          {diseases.map(d => <option key={d.id} value={d.id}>{d.code} — {d.name}</option>)}
        </select>
        <select style={S.select} value={filterPhase} onChange={e => setFilterPhase(e.target.value)}>
          <option value="">Tüm Fazlar</option>
          {['I', 'II', 'III', 'IV', 'Genişletilmiş Erişim'].map(p => <option key={p} value={p}>Faz {p}</option>)}
        </select>
        <select style={S.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Tüm Durumlar</option>
          <option value="open">🟢 Açık</option>
          <option value="closed">🔴 Kapalı</option>
          <option value="suspended">🟡 Askıda</option>
        </select>
      </div>

      {viewMode === 'card' && (
        <div style={S.cardGrid}>
          {filtered.map(trial => {
            const trialDiseases = trial.trial_diseases?.map(td => td.diseases).filter(Boolean) || [];
            return (
              <div key={trial.id} style={S.card} onClick={() => navigate('/trial/' + trial.id)}>
                <div style={S.cardTop}>
                  <div style={S.cardBadges}>
                    <span style={{ ...S.phaseBadge, background: PHASE_COLORS[trial.phase] || '#64748b' }}>Faz {trial.phase || '?'}</span>
                    {trial.trial_arms?.length > 0 && <span style={S.armsBadge}>💊 {trial.trial_arms.length} kol</span>}
                  </div>
                  <span style={S.statusBadge}>{STATUS_LABELS[trial.status]}</span>
                </div>
                <h3 style={S.cardTitle}>{trial.title}</h3>
                {trial.short_code && <p style={S.cardCode}>{trial.short_code}</p>}
                <div style={S.cardMeta}>
                  {trial.branches && <span style={S.branchTag}>🏥 {trial.branches.name}</span>}
                  {trialDiseases.map(d => <span key={d.id} style={S.tag}>🧬 {d.code}</span>)}
                  {trial.nct_number && (
                    <a href={'https://clinicaltrials.gov/study/' + trial.nct_number} target="_blank" rel="noreferrer"
                      style={S.nctTag} onClick={e => e.stopPropagation()}>{trial.nct_number} ↗</a>
                  )}
                </div>
                {trial.trial_arms?.length > 0 && (
                  <div style={S.cardSection}>
                    <p style={S.cardSectionTitle}>💊 Çalışma Kolları</p>
                    {trial.trial_arms.map((arm, i) => (
                      <div key={arm.id} style={S.armRow}>
                        <span style={{ ...S.armDot, background: ARM_COLORS[i % ARM_COLORS.length] }}>{arm.name}</span>
                        <span style={S.armAgents}>{arm.agents}</span>
                      </div>
                    ))}
                  </div>
                )}
                {trial.trial_investigators?.length > 0 && (
                  <div style={S.cardSection}>
                    <p style={S.cardSectionTitle}>🏥 Merkezler ve Araştırmacılar</p>
                    {trial.trial_investigators.map(inv => (
                      <div key={inv.id} style={S.invRow}>
                        <div style={S.invName}>{inv.center_name}{inv.city ? ` — ${inv.city}` : ''}</div>
                        {inv.investigator && <div style={S.invDetail}>👤 {inv.investigator}</div>}
                        {inv.phone && <div style={S.invDetail}>📞 {inv.phone}</div>}
                        {inv.email && <div style={S.invDetail}>✉️ {inv.email}</div>}
                      </div>
                    ))}
                  </div>
                )}
                <div style={S.cardFooter}>Detayları Gör →</div>
              </div>
            );
          })}
          {filtered.length === 0 && <div style={S.empty}>Kriterlere uygun çalışma bulunamadı.</div>}
        </div>
      )}

      {viewMode === 'table' && (
        <div style={S.tableWrapper}>
          <table style={S.table}>
            <thead>
              <tr style={S.tableHeader}>
                <th style={S.th}>Çalışma Adı</th>
                <th style={S.th}>Branş / Faz / Hastalık</th>
                <th style={S.th}>Çalışma Kolları</th>
                <th style={S.th}>Merkezler ve Araştırmacılar</th>
                <th style={S.th}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((trial, i) => {
                const trialDiseases = trial.trial_diseases?.map(td => td.diseases).filter(Boolean) || [];
                return (
                  <tr key={trial.id} style={{ ...S.tr, background: i % 2 === 0 ? 'white' : '#f8fafc' }} onClick={() => navigate('/trial/' + trial.id)}>
                    <td style={S.td}>
                      <span style={S.tableTitle}>{trial.title}</span>
                      {trial.short_code && <span style={S.tableCode}>{trial.short_code}</span>}
                      {trial.nct_number && (
                        <a href={'https://clinicaltrials.gov/study/' + trial.nct_number} target="_blank" rel="noreferrer" style={S.tableNct} onClick={e => e.stopPropagation()}>{trial.nct_number} ↗</a>
                      )}
                    </td>
                    <td style={S.td}>
                      {trial.branches && <span style={S.branchTag}>{trial.branches.name}</span>}
                      <span style={{ ...S.phaseBadge, background: PHASE_COLORS[trial.phase] || '#64748b', display: 'inline-block', marginTop: '0.3rem' }}>Faz {trial.phase || '?'}</span>
                      <div style={{ marginTop: '0.3rem', display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {trialDiseases.map(d => <span key={d.id} style={S.diseaseSmTag}>🧬 {d.code}</span>)}
                      </div>
                    </td>
                    <td style={{ ...S.td, minWidth: '200px' }}>
                      {trial.trial_arms?.length > 0
                        ? trial.trial_arms.map((arm, j) => (
                          <div key={arm.id} style={S.tableArmRow}>
                            <span style={{ ...S.armDotSm, background: ARM_COLORS[j % ARM_COLORS.length] }}>{arm.name}</span>
                            <span style={S.tableArmAgents}>{arm.agents}</span>
                          </div>
                        ))
                        : <span style={S.noData}>—</span>}
                    </td>
                    <td style={{ ...S.td, minWidth: '220px' }}>
                      {trial.trial_investigators?.length > 0
                        ? trial.trial_investigators.map(inv => (
                          <div key={inv.id} style={S.tableInvBlock}>
                            <div style={S.tableInvCenter}>{inv.center_name}{inv.city ? ` (${inv.city})` : ''}</div>
                            {inv.investigator && <div style={S.tableInvDetail}>👤 {inv.investigator}</div>}
                            {inv.phone && <div style={S.tableInvDetail}>📞 {inv.phone}</div>}
                            {inv.email && <div style={S.tableInvDetail}>✉️ {inv.email}</div>}
                          </div>
                        ))
                        : <span style={S.noData}>—</span>}
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.dot, background: trial.status === 'open' ? '#10b981' : trial.status === 'suspended' ? '#f59e0b' : '#ef4444' }}>
                        {STATUS_LABELS[trial.status]}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>Kriterlere uygun çalışma bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const S = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', fontSize: '1.1rem', color: '#64748b' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.5rem', color: '#1e3a5f', margin: 0 },
  subtitle: { color: '#64748b', margin: '0.25rem 0 0', fontSize: '0.875rem' },
  viewToggle: { display: 'flex', gap: '0.5rem' },
  toggleBtn: { padding: '0.5rem 1rem', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' },
  filters: { display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  searchInput: { flex: 2, minWidth: '220px', padding: '0.6rem 1rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.9rem' },
  select: { flex: 1, minWidth: '150px', padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.875rem', background: 'white' },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.25rem' },
  card: { background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', border: '1px solid #e2e8f0' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  cardBadges: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  phaseBadge: { color: 'white', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' },
  armsBadge: { background: '#e0f2fe', color: '#0369a1', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' },
  statusBadge: { fontSize: '0.8rem', color: '#475569' },
  cardTitle: { fontSize: '1rem', color: '#1e293b', margin: '0 0 0.2rem', lineHeight: 1.4, fontWeight: '600' },
  cardCode: { fontSize: '0.8rem', color: '#94a3b8', margin: '0 0 0.6rem' },
  cardMeta: { display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' },
  branchTag: { background: '#f3e8ff', color: '#7c3aed', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '600' },
  tag: { background: '#f1f5f9', color: '#475569', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem' },
  diseaseSmTag: { background: '#d1fae5', color: '#065f46', padding: '0.15rem 0.5rem', borderRadius: '6px', fontSize: '0.72rem', fontWeight: '600' },
  nctTag: { background: '#eff6ff', color: '#1d4ed8', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', textDecoration: 'none' },
  cardSection: { borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', marginTop: '0.75rem' },
  cardSectionTitle: { fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', margin: '0 0 0.5rem' },
  armRow: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.35rem' },
  armDot: { color: 'white', padding: '0.1rem 0.5rem', borderRadius: '999px', fontSize: '0.72rem', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  armAgents: { fontSize: '0.8rem', color: '#334155', lineHeight: 1.4 },
  invRow: { background: '#f0fdf4', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '0.5rem', border: '1px solid #bbf7d0' },
  invName: { fontSize: '0.875rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.4rem' },
  invDetail: { fontSize: '0.85rem', color: '#475569', marginTop: '0.45rem', display: 'flex', alignItems: 'center', gap: '0.4rem', lineHeight: '1.6' },
  cardFooter: { fontSize: '0.8rem', color: '#3b82f6', textAlign: 'right', marginTop: '0.75rem' },
  empty: { gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#94a3b8' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#1e3a5f' },
  th: { padding: '0.875rem 1rem', textAlign: 'left', color: 'white', fontSize: '0.875rem', fontWeight: '600', whiteSpace: 'nowrap' },
  tr: { cursor: 'pointer' },
  td: { padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#334155', borderBottom: '1px solid #e2e8f0', verticalAlign: 'top' },
  tableTitle: { display: 'block', fontWeight: '600', color: '#1e293b', marginBottom: '0.2rem' },
  tableCode: { display: 'block', fontSize: '0.75rem', color: '#94a3b8' },
  tableNct: { display: 'block', fontSize: '0.75rem', color: '#3b82f6', marginTop: '0.2rem' },
  tableArmRow: { display: 'flex', alignItems: 'flex-start', gap: '0.4rem', marginBottom: '0.3rem' },
  armDotSm: { color: 'white', padding: '0.1rem 0.45rem', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  tableArmAgents: { fontSize: '0.78rem', color: '#334155', lineHeight: 1.4 },
  tableInvBlock: { background: '#f0fdf4', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '0.5rem', border: '1px solid #bbf7d0' },
  tableInvCenter: { fontSize: '0.875rem', fontWeight: '700', color: '#1e293b', marginBottom: '0.3rem' },
  tableInvDetail: { fontSize: '0.82rem', color: '#475569', marginTop: '0.4rem', lineHeight: '1.6' },
  dot: { color: 'white', padding: '0.2rem 0.5rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' },
  noData: { color: '#94a3b8', fontSize: '0.8rem' },
};
